import { Injectable, NgZone } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import * as crypto from 'crypto-js';
import { environment } from 'src/environments/environment';
import { StorageKey } from '../models/common';
import { StorageService } from './storage.service';
import { TokenRefreshService } from './token-refresh.service';

@Injectable({ providedIn: 'root' })
export class SseService {
  private readonly appType = 'Broker';

  constructor(
    private zone: NgZone,
    private storageSvc: StorageService,
    private tokenRefreshSvc: TokenRefreshService
  ) {}

  connect<T>(url: string, options?: { emitOnConnect?: boolean }): Observable<T | 'connected'> {
    return new Observable<T | 'connected'>((observer) => {
      const abortController = new AbortController();

      const connectStream = async (allowTokenRefresh = true): Promise<void> => {
        try {
          const response = await fetch(url, {
            method: 'GET',
            headers: this.buildHeaders(),
            signal: abortController.signal,
          });

          if (!response.ok) {
            const errorBody = await response.text().catch(() => '');
            if (
              allowTokenRefresh &&
              this.isExpiredJwtError(response.status, errorBody)
            ) {
              await firstValueFrom(this.tokenRefreshSvc.refreshAccessToken());
              return connectStream(false);
            }

            throw new Error(errorBody || `SSE request failed (${response.status})`);
          }

          if (!response.body) {
            throw new Error('SSE response body is empty');
          }

          if (options?.emitOnConnect) {
            this.zone.run(() => observer.next('connected'));
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const chunks = buffer.split('\n\n');
            buffer = chunks.pop() ?? '';

            for (const chunk of chunks) {
              const payload = this.parseSseChunk(chunk);
              if (!payload) {
                continue;
              }

              this.zone.run(() => observer.next(JSON.parse(payload) as T));
            }
          }

          this.zone.run(() => observer.complete());
        } catch (error) {
          if ((error as Error).name === 'AbortError') {
            return;
          }
          this.zone.run(() => observer.error(error));
        }
      };

      void connectStream();

      return () => abortController.abort();
    });
  }

  private buildHeaders(): Record<string, string> {
    const apiKeyStr = `${environment.encryptionKey}#${this.getCurrentDateTimeInISOFormat()}`;
    const key = crypto.enc.Base64.parse(environment.encryptionKey);
    const encrypted = crypto.AES.encrypt(apiKeyStr, key, {
      mode: crypto.mode.ECB,
      padding: crypto.pad.Pkcs7,
    });
    const apiKey = encrypted.ciphertext.toString(crypto.enc.Base64);

    const headers: Record<string, string> = {
      ApiKey: apiKey,
      Accept: 'text/event-stream',
    };

    const token = this.storageSvc.get(StorageKey.jwt);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      headers['AppType'] = this.appType;
      headers['Role'] = localStorage.getItem('selectedRoleId') || '';
      headers['Company'] = localStorage.getItem('selectedCompanyId') || '';
    }

    return headers;
  }

  private parseSseChunk(chunk: string): string | null {
    const lines = chunk.split('\n');
    const dataLines: string[] = [];

    for (const line of lines) {
      if (line.startsWith('data:')) {
        dataLines.push(line.replace(/^data:\s?/, ''));
      }
    }

    if (!dataLines.length) {
      return null;
    }

    const payload = dataLines.join('\n').trim();
    return payload || null;
  }

  private isExpiredJwtError(status: number, errorBody: string): boolean {
    if (status !== 400 && status !== 500) {
      return false;
    }

    try {
      const parsed = JSON.parse(errorBody) as { title?: string };
      return parsed?.title === 'ExpiredJwtException';
    } catch {
      return false;
    }
  }

  private getCurrentDateTimeInISOFormat(): string {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const hours = String(now.getUTCHours()).padStart(2, '0');
    const minutes = String(now.getUTCMinutes()).padStart(2, '0');
    const seconds = String(now.getUTCSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
  }
}
